import axios from 'axios';

/**
 * publishTikTokPost
 * ----------------------------------------------------------
 * Sends a JSON payload (PULL_FROM_URL workflow).
 *
 * @param {string}        tiktokId   – TikTok profile ID in your DB
 * @param {string}        caption    – post text
 * @param {Array<Object>} files      – [{ key, url, file? }]
 * @param {Object}        opts       – { visibility, allowComments, allowDuet, isBrandedContent, isOrganicBranded }
 * @returns {Promise<Object>} backend response JSON
 */
export async function publishTikTokPost(
  tiktokId,
  caption,
  files = [],
  opts  = {},
) {
  try {
    // Validation
    if (!tiktokId) {
      throw new Error('TikTok account ID is required');
    }
    
    // Use first file's URL (TikTok fetches it)
    const mediaUrl = files[0]?.url || null;
    if (!mediaUrl) {
      throw new Error('TikTok requires a video to post. Please add a video file.');
    }
    
    // --- THIS IS THE UPDATED PART ---
    // The backend now expects all user-configurable settings inside an `options` object.
    const payload = {
      tiktokId,
      caption,
      mediaUrl,
      options: opts, // Pass the entire options object
    };
    // --- END OF UPDATE ---
    
    const { data } = await axios.post(
      '/api/posting/tiktok/publish',
      payload,
      { withCredentials: true },      // send session cookie
    );
    
    console.log('publishTikTokPost success:', data);
    return data;                      // { success: true, tiktok_post_id, ... }
    
  } catch (err) {
    console.error('publishTikTokPost error:', err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to TikTok';
    
    // If this is our custom validation error, preserve it
    if (!err.response && err.message) {
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        errorMessage = err.response.data.details;
      } else if (err.response.data.error?.code) {
        // TikTok specific error codes
        errorMessage = getTikTokErrorByCode(
          err.response.data.error.code, 
          err.response.data.error.error_subcode,
          err.response.data.error.message
        );
      } else if (err.response.data.error_description) {
        // OAuth error format
        errorMessage = err.response.data.error_description;
      } else if (err.response.data.status_msg) {
        // Another TikTok error format
        errorMessage = err.response.data.status_msg;
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      errorMessage = getTikTokErrorByStatus(err.response.status, err.message);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Maps TikTok error codes to user-friendly messages
 */
function getTikTokErrorByCode(code, subCode, fallbackMessage) {
  const errorMap = {
    'access_token_invalid': 'TikTok access token expired. Please reconnect your account.',
    'invalid_auth': 'TikTok authentication failed. Please reconnect your account.',
    'user_not_found': 'TikTok user not found. Account may be restricted or deleted.',
    'permission_denied': 'TikTok permission denied. Please reconnect with video upload permissions.',
    'rate_limit_exceeded': 'TikTok rate limit exceeded. Please wait before posting again.',
    'spam_risk_too_high': 'TikTok detected potential spam. Please try again later.',
    'video_upload_failed': 'TikTok video upload failed. Please check your video file.',
    'video_too_long': 'TikTok video too long. Maximum duration is 10 minutes.',
    'video_too_short': 'TikTok video too short. Minimum duration is 3 seconds.',
    'video_format_not_supported': 'TikTok video format not supported. Use MP4, MOV, or WEBM.',
    'video_size_too_large': 'TikTok video file too large. Maximum size is 287.6 MB.',
    'caption_too_long': 'TikTok caption too long. Maximum 2200 characters allowed.',
    'hashtag_limit_exceeded': 'Too many hashtags. TikTok allows maximum 100 hashtags.',
    'invalid_content': 'TikTok content policy violation. Please review your content.',
    'account_suspended': 'TikTok account suspended. Please check your account status.',
    'daily_upload_limit': 'TikTok daily upload limit reached. Try again tomorrow.',
    'invalid_file_url': 'TikTok cannot access the video URL. Please check the file.',
    'processing_failed': 'TikTok video processing failed. Please try a different video.',
  };
  
  // Check for error subcodes
  if (subCode === 2054002) {
    return 'TikTok scope missing. Please reconnect with video.publish permission.';
  } else if (subCode === 10013) {
    return 'TikTok video processing failed. Please try a different video.';
  }
  
  return errorMap[code] || fallbackMessage || `TikTok error (code: ${code})`;
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
function getTikTokErrorByStatus(status, fallbackMessage) {
  const statusMap = {
    400: 'Invalid TikTok request. Please check your video and caption.',
    401: 'TikTok authentication failed. Please reconnect your account.',
    403: 'TikTok access forbidden. Check your account permissions.',
    404: 'TikTok endpoint not found. Please contact support.',
    413: 'TikTok video file too large. Please use a smaller file.',
    429: 'Too many TikTok requests. Please slow down and try again.',
    500: 'TikTok server error. Please try again later.',
    502: 'TikTok server error. Please try again later.',
    503: 'TikTok service unavailable. Please try again later.',
  };
  
  return statusMap[status] || fallbackMessage || `TikTok error (status: ${status})`;
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show better error dialogs
 */
export function getTikTokErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages with actions
  if (lowerMessage.includes('token expired') || lowerMessage.includes('authentication failed')) {
    return {
      title: 'Connection Expired',
      message: 'Your TikTok connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission denied') || lowerMessage.includes('scope missing')) {
    return {
      title: 'Permission Required',
      message: 'Missing TikTok video upload permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('rate limit')) {
    return {
      title: 'Slow Down',
      message: 'You\'re posting too quickly to TikTok. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('daily upload limit')) {
    return {
      title: 'Daily Limit Reached',
      message: 'You\'ve reached your daily TikTok upload limit. Try again tomorrow.',
      action: 'wait_daily'
    };
  }
  
  if (lowerMessage.includes('spam')) {
    return {
      title: 'Spam Detection',
      message: 'TikTok detected potential spam. Please wait a while before posting again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('video too long')) {
    return {
      title: 'Video Too Long',
      message: 'Your video exceeds TikTok\'s 10-minute limit. Please trim it.',
      action: 'trim'
    };
  }
  
  if (lowerMessage.includes('video too short')) {
    return {
      title: 'Video Too Short',
      message: 'TikTok videos must be at least 3 seconds long.',
      action: 'edit_video'
    };
  }
  
  if (lowerMessage.includes('file too large') || lowerMessage.includes('size too large')) {
    return {
      title: 'File Too Large',
      message: 'Your video is too large. Maximum size is 287.6 MB.',
      action: 'compress'
    };
  }
  
  if (lowerMessage.includes('format not supported')) {
    return {
      title: 'Invalid Format',
      message: 'TikTok doesn\'t support this video format. Use MP4, MOV, or WEBM.',
      action: 'convert'
    };
  }
  
  if (lowerMessage.includes('caption too long')) {
    return {
      title: 'Caption Too Long',
      message: 'Your caption is too long. Maximum 2200 characters allowed.',
      action: 'shorten_caption'
    };
  }
  
  if (lowerMessage.includes('hashtag limit')) {
    return {
      title: 'Too Many Hashtags',
      message: 'TikTok allows maximum 100 hashtags per post.',
      action: 'reduce_hashtags'
    };
  }
  
  if (lowerMessage.includes('content policy') || lowerMessage.includes('invalid content')) {
    return {
      title: 'Content Policy Violation',
      message: 'Your content doesn\'t meet TikTok\'s community guidelines. Please review and modify.',
      action: 'review_content'
    };
  }
  
  if (lowerMessage.includes('account suspended')) {
    return {
      title: 'Account Suspended',
      message: 'Your TikTok account has been suspended. Please check your account status.',
      action: 'check_account'
    };
  }
  
  if (lowerMessage.includes('upload failed') || lowerMessage.includes('processing failed')) {
    return {
      title: 'Upload Failed',
      message: 'Failed to upload your video. Please check your internet connection and try again.',
      action: 'retry'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}

